import { useLayoutEffect, useState, type CSSProperties, type RefObject } from "react";
import { EMAIL_BUILDER_CANVAS_VERTICAL_RAIL_GAP_PX } from "../shell/canvas/emailBuilderCanvasRailLayoutConstants";
import {
  clampEmailBuilderVerticalActionRailLeft,
  EMAIL_BUILDER_VERTICAL_ACTION_RAIL_TOP_OFFSET_PX,
} from "./emailBuilderVerticalActionRailLayout";
import {
  resolveEmailBuilderCanvasRailPinLeft,
  resolveEmailBuilderCanvasRailPinMaxWidthAtLeft,
} from "../shell/canvas/emailBuilderCanvasRailPinLayout";

function pinStylesEqual(
  prev: CSSProperties | null,
  next: CSSProperties,
): boolean {
  return (
    prev !== null &&
    prev.position === next.position &&
    prev.top === next.top &&
    prev.left === next.left &&
    prev.maxWidth === next.maxWidth &&
    prev.maxHeight === next.maxHeight &&
    prev.display === next.display
  );
}

export function useEmailBuilderTouchRailPin(args: {
  enabled: boolean;
  frameRef: RefObject<HTMLElement | null>;
  railRef: RefObject<HTMLElement | null>;
  placement?: "start" | "above";
}): {
  pinned: boolean;
  pinStyle: CSSProperties | null;
} {
  const { enabled, frameRef, railRef, placement = "start" } = args;
  const [pinned, setPinned] = useState(false);
  const [pinStyle, setPinStyle] = useState<CSSProperties | null>(null);

  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    const runLayout = () => {
      const frame = frameRef.current;
      const rail = railRef.current;
      const canvasNode = frame?.closest(".email-builder-canvas");
      if (!frame || !rail || !(canvasNode instanceof HTMLElement)) {
        return;
      }

      const canvasRect = canvasNode.getBoundingClientRect();
      const blockRect = frame.getBoundingClientRect();
      const gap = EMAIL_BUILDER_CANVAS_VERTICAL_RAIL_GAP_PX;
      const railHeight = rail.offsetHeight;
      const railWidth = rail.offsetWidth;

      if (railWidth === 0 || railHeight === 0) {
        return;
      }

      const blockVisibleInCanvas =
        blockRect.bottom > canvasRect.top && blockRect.top < canvasRect.bottom;

      if (!blockVisibleInCanvas) {
        setPinned(false);
        setPinStyle({ display: "none" });
        return;
      }

      setPinned(true);

      const pinTop = canvasRect.top + gap;
      const maxTop = Math.max(pinTop, canvasRect.bottom - gap - railHeight);
      let naturalTop: number;
      let pinLeft: number;
      let maxWidth: number | undefined;
      let maxHeight: number | undefined;

      if (placement === "above") {
        const aboveTop = blockRect.top - railHeight - gap;
        const belowTop = blockRect.bottom + gap;
        naturalTop = aboveTop >= pinTop ? aboveTop : belowTop;
        pinLeft = resolveEmailBuilderCanvasRailPinLeft({
          blockRect,
          canvasRect,
          frameRect: null,
          railWidth,
          leftChromeInsetPx: gap,
          rightChromeInsetPx: 0,
          constrainToEmailFrame: false,
          clampRightEdge: false,
        });
        maxWidth = resolveEmailBuilderCanvasRailPinMaxWidthAtLeft({
          canvasRect,
          leftPx: pinLeft,
        });
      } else {
        naturalTop = blockRect.top + EMAIL_BUILDER_VERTICAL_ACTION_RAIL_TOP_OFFSET_PX;
        pinLeft = clampEmailBuilderVerticalActionRailLeft(
          blockRect.left,
          canvasNode,
          canvasRect,
          railWidth,
        );
        maxHeight = Math.max(
          0,
          canvasRect.bottom - gap - Math.min(Math.max(naturalTop, pinTop), maxTop),
        );
      }

      const fixedTop = Math.min(Math.max(naturalTop, pinTop), maxTop);

      const nextPinStyle: CSSProperties = {
        position: "fixed",
        top: fixedTop,
        left: pinLeft,
        right: "auto",
        transform: "none",
        maxWidth,
        maxHeight,
        visibility: "visible",
        pointerEvents: "auto",
      };

      setPinStyle(prev => (pinStylesEqual(prev, nextPinStyle) ? prev : nextPinStyle));
    };

    let rafId = 0;
    const scheduleLayout = () => {
      if (rafId !== 0) {
        return;
      }
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        runLayout();
      });
    };

    scheduleLayout();

    const frame = frameRef.current;
    const rail = railRef.current;
    const canvasNode = frame?.closest(".email-builder-canvas");
    const mainCanvas =
      canvasNode instanceof HTMLElement
        ? canvasNode.querySelector("#main-canvas")
        : null;

    const observer = new ResizeObserver(scheduleLayout);
    if (frame) {
      observer.observe(frame);
    }
    if (rail) {
      observer.observe(rail);
    }
    if (canvasNode instanceof HTMLElement) {
      observer.observe(canvasNode);
      canvasNode.addEventListener("scroll", scheduleLayout, { passive: true });
      const scaledFrame = canvasNode.querySelector(".email-builder-canvas-frame");
      if (scaledFrame instanceof HTMLElement) {
        observer.observe(scaledFrame);
      }
    }
    if (mainCanvas instanceof HTMLElement) {
      observer.observe(mainCanvas);
      mainCanvas.addEventListener("scroll", scheduleLayout, { passive: true });
    }

    window.addEventListener("resize", scheduleLayout, { passive: true });
    window.addEventListener("scroll", scheduleLayout, { passive: true, capture: true });

    return () => {
      observer.disconnect();
      if (rafId !== 0) {
        cancelAnimationFrame(rafId);
      }
      if (canvasNode instanceof HTMLElement) {
        canvasNode.removeEventListener("scroll", scheduleLayout);
      }
      if (mainCanvas instanceof HTMLElement) {
        mainCanvas.removeEventListener("scroll", scheduleLayout);
      }
      window.removeEventListener("resize", scheduleLayout);
      window.removeEventListener("scroll", scheduleLayout, true);
    };
  }, [enabled, frameRef, placement, railRef]);

  if (!enabled) {
    return { pinned: false, pinStyle: null };
  }

  return { pinned, pinStyle };
}
